import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Pagination, CircularProgress, Chip, useTheme, useMediaQuery, Stack,
  Button, Tooltip, IconButton, InputAdornment, Card, TextField, Autocomplete,
} from "@mui/material";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import config from "../../config";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

const rowsPerPage = 10;

const toLocalDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const mapReportToRow = (report, variety, varietyIndex) => ({
  _id: report._id,
  rowId: `${report._id}-${varietyIndex}`,
  file_name: report.file_name || "",
  person_name: report.person_name || "",
  location: report.location || "",
  business_name: report.business_name || "",
  mobile_number: report.mobile_number || "",
  marketplace_name: report.marketplace_name || report.location || "",
  variety: variety?.variety || "",
  on_hand_rate: variety?.on_hand_rate || "",
  on_hand_quantity: variety?.on_hand_quantity ?? "0",
  combine_rate: variety?.combine_rate || "",
  combine_quantity: variety?.combine_quantity ?? "0",
  buyers: Array.isArray(variety?.buyers)
    ? variety.buyers.map((b) => ({ buyerName: b?.buyerName || "", purchaseQuantity: b?.purchaseQuantity ?? null }))
    : [],
  any_other_important_detail: report.any_other_important_detail || "",
  transcribe: report.transcribe || "",
  callTimeStamp: report.callTimeStamp || "",
  formattedCallTimeStamp: report.formattedCallTimeStamp || report.formattedCallDate || "",
  purchase: report.purchase ?? 0,
  purchaseType: report.purchaseType || "",
  isReport: report.isReport ?? false,
  createdAt: report.createdAt || "",
  updatedAt: report.updatedAt || "",
  __v: report.__v ?? 0,
});

const mapApiReportsToUiRows = (apiReports = []) => {
  if (!Array.isArray(apiReports)) return [];
  return apiReports.flatMap((report) => {
    const varieties = Array.isArray(report.varieties) ? report.varieties : [];
    if (varieties.length === 0) return [mapReportToRow(report, null, 0)];
    return varieties.map((variety, i) => mapReportToRow(report, variety, i));
  });
};

const defaultFilters = {
  date: "",
  person: "",
  mandi: "",
  variety: "",
  state: "",
};

const emptyFilterOptions = { persons: [], mandis: [], varieties: [] };

export default function Report() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportDates, setReportDates] = useState([]);
  const [filterOptions, setFilterOptions] = useState(emptyFilterOptions);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const todayValue = useMemo(() => toLocalDateInputValue(new Date()), []);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [pendingFilters, setPendingFilters] = useState(defaultFilters);

  const [page, setPage] = useState(1);

  const hasPendingChanges = useMemo(
    () => JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters),
    [pendingFilters, appliedFilters]
  );

  const reportsAbortRef = useRef(null);

  const fetchReports = async (filters, pageVal, signal) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("token");
      const params = { page: pageVal, limit: rowsPerPage };
      if (filters.date) params.date = filters.date;
      if (filters.person) params.person = filters.person;
      if (filters.mandi) params.mandi = filters.mandi;
      if (filters.variety) params.variety = filters.variety;
      if (filters.state) params.state = filters.state;

      const response = await axios.get(`${config.apiBaseUrl}/market-survey/get`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal,
      });

      if (response.data?.success) {
        const apiReports = Array.isArray(response.data?.data) ? response.data.data : [];
        setReports(mapApiReportsToUiRows(apiReports));

        const pagination = response.data?.pagination;
        setTotalRecords(pagination ? Number(pagination.totalRecords) || 0 : apiReports.length);
        const responseFilters = response.data?.filters || {};
        const dateEntries = Array.isArray(responseFilters.dates) ? responseFilters.dates : [];
        setReportDates(dateEntries.map((entry) => entry.date).filter(Boolean));

        setFilterOptions({
          persons: Array.isArray(responseFilters.persons) ? responseFilters.persons : [],
          mandis: Array.isArray(responseFilters.mandis) ? responseFilters.mandis : [],
          varieties: Array.isArray(responseFilters.varieties) ? responseFilters.varieties : [],
        });
      } else {
        setReports([]);
        setTotalRecords(0);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === "CanceledError" || error.name === "AbortError") return;
      console.error("Fetch reports error:", error);
      setReports([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    reportsAbortRef.current = controller;

    const timeoutId = setTimeout(() => {
      fetchReports(appliedFilters, page, controller.signal);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [appliedFilters, page]);

  const setPendingField = (field) => (value) =>
    setPendingFilters((prev) => ({ ...prev, [field]: value }));

  const handlePendingDateChange = (newValue) => {
    if (!newValue) {
      setPendingField("date")("");
      return;
    }
    const value = newValue.format("YYYY-MM-DD");
    if (value > todayValue) return;
    setPendingField("date")(value);
  };

  const handleToday = () => setPendingField("date")(todayValue);
  const handleAllDates = () => setPendingField("date")("");

  const clearAllFilters = () => {
    setPendingFilters({ date: "", person: "", mandi: "", variety: "", state: "" });
  };

  const applyFilters = () => {
    if (!hasPendingChanges) return;
    setAppliedFilters(pendingFilters);
    setPage(1);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const match = String(value).match(/^(\d{2})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})$/);
    if (!match) return "-";
    const [, yy, mm, dd, hh, min, ss] = match;
    const date = new Date(2000 + Number(yy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss));
    return date.toLocaleDateString();
  };

  const pageCount = Math.max(1, Math.ceil(totalRecords / rowsPerPage));

  const hasActiveAppliedFilters = Boolean(
    appliedFilters.date || appliedFilters.person || appliedFilters.mandi || appliedFilters.variety || appliedFilters.state
  );
  const hasAnyPendingValues = Boolean(
    pendingFilters.date || pendingFilters.person || pendingFilters.mandi || pendingFilters.variety || pendingFilters.state
  );

  const withSelectedValue = (options, selected) =>selected && !options.includes(selected) ? [selected, ...options] : options;


  const visibleColumnCount = isMobile ? 5 : 11;

  const mobileHeadCellSx = {
    px: 1.1,
    py: 1,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    color: "rgba(15,23,42,0.55)",
    background: "#F8FAFC",
    borderBottom: "2px solid rgba(15,23,42,0.08)",
    borderRight: "1px solid rgba(15,23,42,0.10)",
    whiteSpace: "nowrap",
  };
  const mobileBodyCellSx = {
    px: 1.1,
    py: 1.1,
    fontSize: "0.76rem",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    borderRight: "1px solid rgba(15,23,42,0.08)",
  };
  const stickyColSx = isMobile
    ? {
        position: "sticky",
        left: 0,
        zIndex: 1,
        boxShadow: "2px 0 4px -2px rgba(15,23,42,0.18)",
      }
    : {};

  const NoData = () => (
    <Paper sx={{ p: 5, textAlign: "center", borderRadius: 3 }}>
      <Typography variant="h6">No Reports Found</Typography>
    </Paper>
  );

  const RateQtyRow = ({ label, rate, qty }) => (
    <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
      <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "rgba(15,23,42,0.45)", letterSpacing: "0.02em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#0F172A", whiteSpace: "nowrap" }}>
        {rate || "-"}
        <Box component="span" sx={{ color: "rgba(15,23,42,0.45)", fontWeight: 500 }}> / {qty || "-"}</Box>
      </Typography>
    </Stack>
  );

  const BuyerChips = ({ buyers, compact = false }) => {
    if (!Array.isArray(buyers) || buyers.length === 0) {
      return <Typography variant="body2" sx={compact ? { fontSize: "0.74rem" } : {}}>-</Typography>;
    }
    const visible = compact ? buyers.slice(0, 2) : buyers;
    const extraCount = compact ? buyers.length - visible.length : 0;
    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
        {visible.map((b, i) =>
          b?.buyerName ? (
            <Chip
              key={i}
              size="small"
              variant="outlined"
              label={
                b.purchaseQuantity !== null && b.purchaseQuantity !== undefined && b.purchaseQuantity !== ""
                  ? `${b.buyerName} (${b.purchaseQuantity})`
                  : b.buyerName
              }
              sx={{
                fontWeight: 500,
                ...(compact && { height: 22, fontSize: "0.64rem", "& .MuiChip-label": { px: 0.7 } }),
              }}
            />
          ) : null
        )}
        {extraCount > 0 && (
          <Chip
            size="small"
            label={`+${extraCount}`}
            sx={{ height: 22, fontSize: "0.64rem", fontWeight: 700, "& .MuiChip-label": { px: 0.7 } }}
          />
        )}
      </Stack>
    );
  };


  const ReportCalendarDay = (props) => {
    const { day, outsideCurrentMonth, selected, ...other } = props;
    const dateString = day.format("YYYY-MM-DD");
    const hasReport = reportDates.includes(dateString);

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={selected}
        sx={{
          ...(hasReport && !selected && !outsideCurrentMonth && {
            color: "#16A34A", fontWeight: 800,
            "&:hover": { backgroundColor: "rgba(22,163,74,0.10)" },
          }),
          ...(selected && !outsideCurrentMonth && {
            backgroundColor: "#2563EB !important", color: "#FFFFFF !important", fontWeight: 800,
            border: "2px solid #0F172A", boxSizing: "border-box",
            "&:hover": { backgroundColor: "#1D4ED8 !important" },
            "&:focus": { backgroundColor: "#2563EB !important" },
          }),
        }}
      />
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", overflowX: "hidden" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5, md: 3 }, background: "#F4F6F8", overflowX: "hidden", width: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1.5} mb={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ border: "1px solid #E0E0E0", borderRadius: "12px" }}>
                <MenuRoundedIcon />
              </IconButton>
            )}
            <Typography variant={isMobile ? "h6" : "h4"} fontWeight={800}>Market Survey Reports</Typography>
            {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Stack>

          <Button
            variant="contained"
            startIcon={<FactCheckRoundedIcon />}
            onClick={() => navigate("/validation-logs")}
            sx={{
              borderRadius: "12px", px: 3, py: 1.2, textTransform: "none", fontWeight: 600, boxShadow: "none",
              background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
              "&:hover": { background: "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)" },
            }}
          >
            Validation Logs
          </Button>
        </Stack>

        <Card
          elevation={0}
          sx={{ borderRadius: { xs: "14px", sm: "16px" }, p: { xs: 1.75, sm: 2.25 }, mb: 2, border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 6px 18px rgba(15,23,42,0.04)" }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: { xs: 1.75, sm: 2 } }}>
            <Box
              sx={{
                width: 38, height: 38, flexShrink: 0, borderRadius: "11px", display: "flex", alignItems: "center",
                justifyContent: "center", color: "#fff", background: "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",
              }}
            >
              <CalendarMonthRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#0F172A" }}>Filters</Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(15,23,42,0.5)" }}>
                {hasActiveAppliedFilters ? "Showing filtered results" : "Showing all reports"}
                {appliedFilters.date ? ` for ${appliedFilters.date}` : " for all dates"}
                {hasPendingChanges ? " · unapplied changes — click Apply" : ""}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date"
                value={pendingFilters.date ? dayjs(pendingFilters.date) : null}
                onChange={handlePendingDateChange}
                maxDate={dayjs(todayValue)}
                disabled={loading}
                slots={{ day: ReportCalendarDay }}
                slotProps={{
                  textField: {
                    size: "small", fullWidth: true,
                    InputLabelProps: { shrink: true },
                    placeholder: "All dates",
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EventRoundedIcon sx={{ fontSize: 19, color: "rgba(15,23,42,0.45)" }} />
                        </InputAdornment>
                      ),
                    },
                  },
                  field: { clearable: true },
                }}
                sx={{
                  width: "100%", maxWidth: { xs: "100%", sm: 210 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px", background: "rgba(37,99,235,0.04)",
                    "& fieldset": { borderColor: "rgba(15,23,42,0.12)" },
                    "&:hover fieldset": { borderColor: "rgba(37,99,235,0.4)" },
                    "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                  },
                  "& input": { cursor: loading ? "not-allowed" : "pointer" },
                }}
              />
            </LocalizationProvider>

            <Button
              size="small"
              variant="outlined"
              disabled={pendingFilters.date === todayValue || loading}
              onClick={handleToday}
              sx={{
                borderRadius: "10px", textTransform: "none", fontWeight: 600,
                borderColor: "rgba(15,23,42,0.12)", color: "#0F172A", whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Today
            </Button>

            <Button
              size="small"
              variant="outlined"
              disabled={!pendingFilters.date || loading}
              onClick={handleAllDates}
              sx={{
                borderRadius: "10px", textTransform: "none", fontWeight: 600,
                borderColor: "rgba(15,23,42,0.12)", color: "#0F172A", whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              All Dates
            </Button>
          </Stack>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.25, sm: 1.5 }, justifyContent: { xs: "center", md: "center", lg: "flex-start" } }}>
            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(50% - 12px)", lg: "calc(20% - 15px)" } }}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={loading}
                options={withSelectedValue(filterOptions.persons, pendingFilters.person)}
                value={pendingFilters.person || null}
                onChange={(_, newValue) => setPendingField("person")(newValue || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Person" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                )}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(50% - 12px)", lg: "calc(20% - 15px)" } }}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={loading}
                options={withSelectedValue(filterOptions.mandis, pendingFilters.mandi)}
                value={pendingFilters.mandi || null}
                onChange={(_, newValue) => setPendingField("mandi")(newValue || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Mandi" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                )}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(50% - 12px)", lg: "calc(20% - 15px)" } }}>
              <Autocomplete
                fullWidth
                size="small"
                disabled={loading}
                options={withSelectedValue(filterOptions.varieties, pendingFilters.variety)}
                value={pendingFilters.variety || null}
                onChange={(_, newValue) => setPendingField("variety")(newValue || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Variety" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                )}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(50% - 12px)", lg: "calc(20% - 15px)" } }}>
              <TextField
                fullWidth size="small" label="State"
                value={pendingFilters.state}
                onChange={(e) => setPendingField("state")(e.target.value)}
                disabled={loading}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Box>

            <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(50% - 12px)", lg: "calc(20% - 15px)" }, display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                fullWidth
                disabled={!hasPendingChanges || loading}
                onClick={applyFilters}
                startIcon={<CheckRoundedIcon sx={{ fontSize: 17 }} />}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontWeight: 600, boxShadow: "none",
                  background: "#16A34A", whiteSpace: "nowrap", height: 40, "&:hover": { background: "#15803D" },
                }}
              >
                Apply
              </Button>

              <Tooltip title="Clear all filters including date — click Apply to run">
                <span style={{ width: "100%" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<FilterAltOffRoundedIcon />}
                    disabled={!hasAnyPendingValues || loading}
                    onClick={clearAllFilters}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", height: 40 }}
                  >
                    Clear
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {hasActiveAppliedFilters && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: { xs: 1.5, sm: 1.75 }, rowGap: 1 }}>
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(15,23,42,0.5)", alignSelf: "center", mr: 0.5 }}>Applied:</Typography>
              <Chip size="small" label={appliedFilters.date ? `Date: ${appliedFilters.date}` : "Date: All"} sx={{ fontWeight: 600 }} />
              {appliedFilters.person && <Chip size="small" label={`Person: ${appliedFilters.person}`} sx={{ fontWeight: 600 }} />}
              {appliedFilters.mandi && <Chip size="small" label={`Mandi: ${appliedFilters.mandi}`} sx={{ fontWeight: 600 }} />}
              {appliedFilters.variety && <Chip size="small" label={`Variety: ${appliedFilters.variety}`} sx={{ fontWeight: 600 }} />}
              {appliedFilters.state && <Chip size="small" label={`State: ${appliedFilters.state}`} sx={{ fontWeight: 600 }} />}
            </Stack>
          )}
        </Card>

        <Box sx={{ position: "relative" }}>
          {loading && (
            <Box sx={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(244,246,248,0.6)", borderRadius: 3 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          <Box sx={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? "none" : "auto", transition: "opacity 0.15s ease" }}>
            {isMobile && reports.length > 0 && (
              <Typography sx={{ fontSize: "0.68rem", color: "rgba(15,23,42,0.45)", mb: 0.75, ml: 0.25 }}>
                Swipe left for more →
              </Typography>
            )}
            <Paper
              elevation={3}
              sx={{
                maxHeight: "75vh",
                overflow: "auto",
                borderRadius: 2,
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table stickyHeader size={isMobile ? "small" : "medium"}>
                <TableHead>
                  {isMobile ? (
                    <TableRow>
                      <TableCell sx={{ ...mobileHeadCellSx, ...stickyColSx, background: "#F8FAFC", minWidth: 96 }}>
                        Mandi
                      </TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 84 }}>Variety</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 130 }}>Rate / Qty</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 140 }}>Buyers</TableCell>
                      <TableCell sx={{ ...mobileHeadCellSx, minWidth: 84, borderRight: "none" }}>Call Date</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.10)" }}>Person</TableCell>
                      <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.10)" }}>Business</TableCell>
                      <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.10)" }}>Mandi</TableCell>
                      <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.10)" }}>Mobile</TableCell>
                      <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.10)" }}>Variety</TableCell>
                      <TableCell sx={{ minWidth: 150, borderRight: "1px solid rgba(15,23,42,0.10)" }}>On Hand Rate</TableCell>
                      <TableCell sx={{ minWidth: 150, borderRight: "1px solid rgba(15,23,42,0.10)" }}>On Hand Qty</TableCell>
                      <TableCell sx={{ minWidth: 150, borderRight: "1px solid rgba(15,23,42,0.10)" }}>Combine Rate</TableCell>
                      <TableCell sx={{ minWidth: 150, borderRight: "1px solid rgba(15,23,42,0.10)" }}>Combine Qty</TableCell>
                      <TableCell sx={{ minWidth: 220, borderRight: "1px solid rgba(15,23,42,0.10)" }}>Buyers</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>Call Date</TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {reports.length > 0 ? (
                    reports.map((row, idx) => {
                      const key = row.rowId || row._id;
                      const zebraSx = isMobile && idx % 2 === 1 ? { background: "rgba(15,23,42,0.015)" } : {};

                      if (isMobile) {
                        return (
                          <TableRow key={key} sx={zebraSx}>
                            <TableCell
                              sx={{
                                ...mobileBodyCellSx,
                                ...stickyColSx,
                                background: idx % 2 === 1 ? "#FBFCFD" : "#fff",
                                fontWeight: 600,
                              }}
                            >
                              {row.marketplace_name || "-"}
                            </TableCell>
                            <TableCell sx={mobileBodyCellSx}>
                              <Chip
                                label={row.variety || "-"}
                                size="small"
                                color={row.variety ? "primary" : "default"}
                                sx={{ height: 22, fontSize: "0.64rem", "& .MuiChip-label": { px: 0.7 } }}
                              />
                            </TableCell>
                            <TableCell sx={mobileBodyCellSx}>
                              <Stack spacing={0.4}>
                                <RateQtyRow label="ON HAND" rate={row.on_hand_rate} qty={row.on_hand_quantity} />
                                <RateQtyRow label="COMBINE" rate={row.combine_rate} qty={row.combine_quantity} />
                              </Stack>
                            </TableCell>
                            <TableCell sx={mobileBodyCellSx}>
                              <BuyerChips buyers={row.buyers} compact />
                            </TableCell>
                            <TableCell sx={{ ...mobileBodyCellSx, whiteSpace: "nowrap", borderRight: "none" }}>
                              {row.formattedCallTimeStamp ? row.formattedCallTimeStamp.split(" ")[0] : formatDate(row.callTimeStamp)}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return (
                        <TableRow key={key} hover>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.person_name || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.business_name || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.marketplace_name || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.mobile_number || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>
                            <Chip label={row.variety || "-"} size="small" color={row.variety ? "primary" : "default"} />
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.on_hand_rate || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.on_hand_quantity || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.combine_rate || "-"}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid rgba(15,23,42,0.08)" }}>{row.combine_quantity || "-"}</TableCell>
                          <TableCell sx={{ minWidth: 220, borderRight: "1px solid rgba(15,23,42,0.08)" }}>
                            <BuyerChips buyers={row.buyers} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.formattedCallTimeStamp ? row.formattedCallTimeStamp.split(" ")[0] : formatDate(row.callTimeStamp)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={visibleColumnCount}>
                        <NoData />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            {pageCount > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  disabled={loading}
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}